import React, { useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelectorMachineContext } from '@printdeal/product-selector';
import { dispatchDataLayerEntry } from '../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import ErrorAlert from '../../../error-handling/ErrorAlert';

const errorMessages = defineMessages({
  pricing: {
    id: 'product.selector.error.pricing',
    defaultMessage: 'We are unable to show the prices for your current selection.',
  },
  servicePricing: {
    id: 'product.selector.error.servicePricing',
    defaultMessage: 'We are unable to show the service prices.',
  },
  unresolvedValidation: {
    id: 'product.selector.error.unresolvedValidation',
    defaultMessage: 'Your selection cannot be resolved, not even when we try to unset some other options. Please try again with a different selection.',
  },
  unknownValidation: {
    id: 'product.selector.error.unknownValidation',
    defaultMessage: 'Something unknown went wrong while validating your selection.',
  },
  unknown: {
    id: 'product.selector.error.unknown',
    defaultMessage: 'Something went wrong with the selector.',
  },
});

const SelectorError = ({ contentfulId } : { contentfulId: string }) => {
  const intl = useIntl();
  const error = useSelectorMachineContext((state) => state.context.error);
  const isErroring = useSelectorMachineContext((state) => state.matches({ STARTED: 'ERRORING' }));

  const translation = useMemo(() => {
    switch (error) {
      case 'PricingError':
        return errorMessages.pricing;
      case 'ServicePricingError':
        return errorMessages.servicePricing;
      case 'UnresolvedValidationError':
        return errorMessages.unresolvedValidation;
      case 'UnknownValidationError':
        return errorMessages.unknownValidation;
      default:
        return errorMessages.unknown;
    }
  }, [error]);

  if (isErroring) {
    const { id, defaultMessage } = translation;
    dispatchDataLayerEntry(
      'selector',
      'error',
      {
        msg: intl.formatMessage(translation),
        contentfulId,
      },
    );

    return (
      <ErrorAlert className="pd-my-3 pd-px-3" showDebugId>
        <FormattedMessage id={id} defaultMessage={defaultMessage} />
      </ErrorAlert>
    );
  }

  return null;
};

export default SelectorError;
